import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  AttachMoney as MoneyIcon,
  CreditCard as CardIcon,
  QrCode as QrIcon,
} from '@mui/icons-material';
import { useSalesModal } from '../../../hooks/useSalesModal';
import { roundMoney } from '../../../utils/money';
import {
  applyCommercialPricingDraft,
  clampUniformDiscountValue,
  doesEditTotalFallBelowPaid,
  getMaxExtraDiscountAmount,
  getMaxUniformDiscountAmount,
  hasZeroPriceLine,
  toPerPieceDraft,
  isLineDiscountInvalid,
  isUniformAmountDiscountInvalid,
  linePricingDisplayBase,
  normalizeAdjustmentType,
  projectUniformAdjustedPrices,
} from '../../../utils/sales/line-pricing.utils';
import { calculateOrderChargesTotal } from '../../../utils/sales/order-charges.utils';
import { formatPriceAdjustmentDisplay } from '../../../utils/formatters/money';
import { PaymentMethod } from '../../../api/models/PaymentMethod';
import { PAYMENT_METHODS } from '../../../utils/constants/paymentMethods';
import { useOtherPaymentMethods } from '../../../hooks/useOtherPaymentMethods';
import { DiscountConfig, MarkupConfig, SalesStep2FlowPhase } from '../../../types/sales.types';

const PAYMENT_METHOD_OPTIONS = [
  { id: 'CASH', name: 'Efectivo', icon: MoneyIcon, color: 'success.main' },
  { id: 'CARD', name: 'Tarjeta', icon: CardIcon, color: 'primary.main' },
  { id: 'QR', name: 'QR', icon: QrIcon, color: 'secondary.main' },
];

export type { SalesStep2FlowPhase };

interface PricingConfigDraft {
  markupEnabled: boolean;
  allowIndividualMarkups: boolean;
  markupType: 'PERCENTAGE' | 'AMOUNT';
  markupValue: number;
  discountEnabled: boolean;
  allowIndividualDiscounts: boolean;
  discountType: 'PERCENTAGE' | 'AMOUNT';
  discountValue: number;
}

const INITIAL_PRICING_DRAFT: PricingConfigDraft = {
  markupEnabled: false,
  allowIndividualMarkups: false,
  markupType: 'AMOUNT',
  markupValue: 0,
  discountEnabled: false,
  allowIndividualDiscounts: false,
  discountType: 'AMOUNT',
  discountValue: 0,
};

function pricingDraftFromConfigs(
  markupConfig: MarkupConfig,
  discountConfig: DiscountConfig,
): PricingConfigDraft {
  return {
    markupEnabled: markupConfig.enabled,
    allowIndividualMarkups: markupConfig.allowIndividualMarkups,
    markupType: markupConfig.orderMarkupType === 'PERCENTAGE' ? 'PERCENTAGE' : 'AMOUNT',
    markupValue: markupConfig.orderMarkupValue,
    discountEnabled: discountConfig.enabled,
    allowIndividualDiscounts: discountConfig.allowIndividualDiscounts,
    discountType: discountConfig.orderDiscountType === 'PERCENTAGE' ? 'PERCENTAGE' : 'AMOUNT',
    discountValue: discountConfig.orderDiscountValue,
  };
}

function getProjectedAdjustedPrices(
  products: Array<{ originalPrice: number; finalPrice: number }>,
  draft: PricingConfigDraft,
  useSavedFinals: boolean,
): Array<{ adjustedPrice: number }> {
  const bases = products.map((product) => ({
    originalPrice: useSavedFinals ? product.finalPrice : product.originalPrice,
  }));

  if (!draft.markupEnabled || draft.allowIndividualMarkups) {
    return bases.map((product) => ({ adjustedPrice: roundMoney(product.originalPrice) }));
  }

  return projectUniformAdjustedPrices(bases, {
    markupType: draft.markupType,
    markupValue: draft.markupValue,
  });
}

export const useSalesStep2 = () => {
  const {
    state,
    orderSummary,
    updatePaymentConfig,
    updateProductMarkup,
    updateProductDiscount,
    resetAllLinePricing,
    restoreEditPricingBaseline,
    commitCommercialPricingConfig,
    setStep2FlowPhase,
    setStep2PaymentsLocked,
    clearPayments,
    addPayment,
    removePayment,
    updatePayment,
    addOrderCharge,
    updateOrderCharge,
    removeOrderCharge,
    chargeCategories,
    canProceedToStep3,
    nextStep,
    previousStep,
  } = useSalesModal();

  const flowPhase = state.step2FlowPhase;
  const isEditMode = state.mode === 'edit';
  const [pricingDraft, setPricingDraft] = useState<PricingConfigDraft>(() =>
    state.mode === 'edit'
      ? pricingDraftFromConfigs(state.markupConfig, state.discountConfig)
      : INITIAL_PRICING_DRAFT,
  );
  const pricingDraftRef = useRef(pricingDraft);
  pricingDraftRef.current = pricingDraft;
  const seededOrderIdRef = useRef<number | null>(
    state.mode === 'edit' ? state.editingOrderId : null,
  );

  // Edit seeds the toolbar from saved increment/discount, like nueva venta after Aplicar.
  useEffect(() => {
    if (state.mode !== 'edit' || state.editingOrderId == null) {
      if (seededOrderIdRef.current != null) {
        seededOrderIdRef.current = null;
        setPricingDraft(INITIAL_PRICING_DRAFT);
      }
      return;
    }
    if (seededOrderIdRef.current === state.editingOrderId) {
      return;
    }
    seededOrderIdRef.current = state.editingOrderId;
    setPricingDraft(pricingDraftFromConfigs(state.markupConfig, state.discountConfig));
  }, [state.mode, state.editingOrderId, state.markupConfig, state.discountConfig]);

  const { otherPaymentMethods, getPaymentMethodName } = useOtherPaymentMethods();
  const [showOtherMethodsDialog, setShowOtherMethodsDialog] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);
  const [showPaymentAmountDialog, setShowPaymentAmountDialog] = useState(false);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState<string | null>(null);

  const updatePricingDraft = useCallback((partial: Partial<PricingConfigDraft>) => {
    setPricingDraft((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetPricingDraft = useCallback(() => {
    setPricingDraft(INITIAL_PRICING_DRAFT);
  }, []);

  const closePaymentUi = useCallback(() => {
    setShowPaymentAmountDialog(false);
    setShowOtherMethodsDialog(false);
    setPendingPaymentMethod(null);
    setEditingPaymentId(null);
  }, []);

  const projectedAdjustedProducts = useMemo(
    () => getProjectedAdjustedPrices(state.selectedProducts, pricingDraft, isEditMode),
    [isEditMode, pricingDraft, state.selectedProducts],
  );

  // Alguna pieza sin precio (Bs 0) sobre la base de cálculo (catálogo al crear, precio ya
  // aceptado en edición): los ajustes al total se reparten por precio, así que no se usan y el
  // incremento/descuento se cargan por pieza.
  const forcePerPiece = useMemo(
    () =>
      hasZeroPriceLine(
        state.selectedProducts.map((product) => ({
          originalPrice: isEditMode
            ? (product.editLayerBasePrice ?? product.finalPrice)
            : product.originalPrice,
        })),
      ),
    [isEditMode, state.selectedProducts],
  );

  // Si aparece una pieza en 0 con un ajuste al total activo, ese ajuste pasa a "por pieza".
  useEffect(() => {
    if (forcePerPiece) {
      setPricingDraft(toPerPieceDraft);
    }
  }, [forcePerPiece]);

  const chargesTotal = useMemo(
    () => calculateOrderChargesTotal(state.orderCharges),
    [state.orderCharges],
  );

  const maxUniformDiscountAmount = useMemo(() => {
    const adjustedProducts = flowPhase === 1 ? projectedAdjustedProducts : state.selectedProducts;
    if (!isEditMode) {
      return getMaxUniformDiscountAmount(adjustedProducts);
    }
    return getMaxExtraDiscountAmount({
      adjustedPrices: adjustedProducts.map((product) => product.adjustedPrice),
      chargesTotal,
      amountPaid: state.originalAmountPaid,
    });
  }, [
    chargesTotal,
    flowPhase,
    isEditMode,
    projectedAdjustedProducts,
    state.originalAmountPaid,
    state.selectedProducts,
  ]);

  const projectedEditLayerProductsTotal = useMemo(() => {
    if (!isEditMode) {
      return 0;
    }
    const fields = applyCommercialPricingDraft(
      state.selectedProducts.map((product) => ({
        id: product.warehouseInventoryProduct.id,
        originalPrice: product.finalPrice,
      })),
      pricingDraft,
    );
    return roundMoney(
      [...fields.values()].reduce((sum, fieldsForLine) => sum + fieldsForLine.finalPrice, 0),
    );
  }, [isEditMode, pricingDraft, state.selectedProducts]);

  const editDiscountBreachesPaid =
    isEditMode &&
    pricingDraft.discountEnabled &&
    !pricingDraft.allowIndividualDiscounts &&
    pricingDraft.discountValue > 0 &&
    doesEditTotalFallBelowPaid(
      projectedEditLayerProductsTotal,
      chargesTotal,
      state.originalAmountPaid,
    );

  const canConfirmConfigSection = useMemo(() => {
    if (state.selectedProducts.length === 0) {
      return false;
    }

    if (!pricingDraft.discountEnabled || pricingDraft.allowIndividualDiscounts) {
      return true;
    }

    if (pricingDraft.discountValue <= 0) {
      return true;
    }

    if (isEditMode) {
      return !editDiscountBreachesPaid;
    }

    if (pricingDraft.discountType === 'PERCENTAGE') {
      return pricingDraft.discountValue <= 99;
    }

    return !isUniformAmountDiscountInvalid(
      flowPhase === 1 ? projectedAdjustedProducts : state.selectedProducts,
      pricingDraft.discountValue,
    );
  }, [
    editDiscountBreachesPaid,
    flowPhase,
    isEditMode,
    pricingDraft,
    projectedAdjustedProducts,
    state.selectedProducts,
  ]);

  const discountExceedsTotal =
    pricingDraft.discountEnabled &&
    !pricingDraft.allowIndividualDiscounts &&
    pricingDraft.discountValue > 0 &&
    (isEditMode
      ? editDiscountBreachesPaid
      : pricingDraft.discountType === 'AMOUNT' &&
        isUniformAmountDiscountInvalid(projectedAdjustedProducts, pricingDraft.discountValue));

  const hasCommercialConfig = useMemo(
    () =>
      flowPhase === 1
        ? pricingDraft.markupEnabled || pricingDraft.discountEnabled
        : state.markupConfig.enabled || state.discountConfig.enabled,
    [
      flowPhase,
      pricingDraft.discountEnabled,
      pricingDraft.markupEnabled,
      state.discountConfig.enabled,
      state.markupConfig.enabled,
    ],
  );

  const hasInvalidLinePricing = useMemo(() => {
    if (flowPhase < 2 || !hasCommercialConfig) {
      return false;
    }

    const invalidMarkup = state.selectedProducts.some(
      (product) => product.adjustedPrice + 0.01 < linePricingDisplayBase(product),
    );

    if (invalidMarkup) {
      return true;
    }

    if (isEditMode) {
      const productsTotal = roundMoney(
        state.selectedProducts.reduce((sum, product) => sum + roundMoney(product.finalPrice), 0),
      );
      if (doesEditTotalFallBelowPaid(productsTotal, chargesTotal, state.originalAmountPaid)) {
        return true;
      }
    }

    if (!state.discountConfig.enabled) {
      return false;
    }

    if (
      !state.discountConfig.allowIndividualDiscounts &&
      state.discountConfig.orderDiscountType === 'AMOUNT'
    ) {
      return isUniformAmountDiscountInvalid(
        state.selectedProducts,
        state.discountConfig.orderDiscountValue,
      );
    }

    return state.selectedProducts.some((product) =>
      isLineDiscountInvalid(product.adjustedPrice, product.discountType, product.discountValue),
    );
  }, [
    chargesTotal,
    flowPhase,
    hasCommercialConfig,
    isEditMode,
    state.discountConfig,
    state.originalAmountPaid,
    state.selectedProducts,
  ]);

  const canConfirmProductsSection = useMemo(() => {
    return state.selectedProducts.length > 0 && !hasInvalidLinePricing;
  }, [state.selectedProducts.length, hasInvalidLinePricing]);

  const commitPricingConfig = useCallback(() => {
    if (!canConfirmConfigSection) return;
    closePaymentUi();
    const draft = pricingDraftRef.current;
    commitCommercialPricingConfig({
      ...draft,
      markupType: normalizeAdjustmentType(draft.markupType),
      discountType: normalizeAdjustmentType(draft.discountType),
    });
  }, [canConfirmConfigSection, closePaymentUi, commitCommercialPricingConfig]);

  const confirmProductsSection = useCallback(() => {
    if (!canConfirmProductsSection) return;
    clearPayments();
    closePaymentUi();
    setStep2PaymentsLocked(false);
    setStep2FlowPhase(3);
  }, [
    canConfirmProductsSection,
    clearPayments,
    closePaymentUi,
    setStep2FlowPhase,
    setStep2PaymentsLocked,
  ]);

  const confirmChargesSection = useCallback(() => {
    clearPayments();
    closePaymentUi();
    setStep2PaymentsLocked(false);
    setStep2FlowPhase(4);
  }, [clearPayments, closePaymentUi, setStep2FlowPhase, setStep2PaymentsLocked]);

  const confirmPaymentConfigSection = useCallback(() => {
    clearPayments();
    closePaymentUi();
    setStep2PaymentsLocked(false);
    setStep2FlowPhase(5);
  }, [clearPayments, closePaymentUi, setStep2FlowPhase, setStep2PaymentsLocked]);

  const unlockConfigForEdit = useCallback(() => {
    if (!isEditMode) {
      resetAllLinePricing();
      clearPayments();
    } else {
      restoreEditPricingBaseline();
    }
    resetPricingDraft();
    closePaymentUi();
    setStep2PaymentsLocked(false);
    setStep2FlowPhase(1);
  }, [
    isEditMode,
    clearPayments,
    closePaymentUi,
    resetAllLinePricing,
    resetPricingDraft,
    restoreEditPricingBaseline,
    setStep2FlowPhase,
    setStep2PaymentsLocked,
  ]);

  const unlockProductsForEdit = useCallback(() => {
    if (!isEditMode) {
      clearPayments();
    }
    closePaymentUi();
    setStep2PaymentsLocked(false);
    setStep2FlowPhase(2);
  }, [isEditMode, clearPayments, closePaymentUi, setStep2FlowPhase, setStep2PaymentsLocked]);

  const unlockChargesForEdit = useCallback(() => {
    if (!isEditMode) {
      clearPayments();
    }
    closePaymentUi();
    setStep2PaymentsLocked(false);
    setStep2FlowPhase(3);
  }, [isEditMode, clearPayments, closePaymentUi, setStep2FlowPhase, setStep2PaymentsLocked]);

  const unlockPaymentConfigForEdit = useCallback(() => {
    if (isEditMode) return;
    clearPayments();
    closePaymentUi();
    setStep2PaymentsLocked(false);
    setStep2FlowPhase(4);
  }, [isEditMode, clearPayments, closePaymentUi, setStep2FlowPhase, setStep2PaymentsLocked]);

  const unlockPaymentMethodsForEdit = useCallback(() => {
    if (isEditMode) return;
    closePaymentUi();
    setStep2PaymentsLocked(false);
  }, [isEditMode, closePaymentUi, setStep2PaymentsLocked]);

  const goToPreviousStep2Section = useCallback(() => {
    if (flowPhase <= 1) return;
    if (flowPhase === 2) {
      setStep2FlowPhase(1);
      return;
    }
    if (flowPhase === 3) {
      setStep2FlowPhase(hasCommercialConfig ? 2 : 1);
      return;
    }
    // Payment phases (create only): step back to charges
    setStep2FlowPhase(3);
  }, [flowPhase, hasCommercialConfig, setStep2FlowPhase]);

  const handleMarkupToggle = useCallback(
    (enabled: boolean) => {
      if (!enabled) {
        updatePricingDraft({
          markupEnabled: false,
          allowIndividualMarkups: false,
          markupType: 'AMOUNT',
          markupValue: 0,
        });
        return;
      }
      updatePricingDraft({ markupEnabled: true, allowIndividualMarkups: forcePerPiece });
    },
    [forcePerPiece, updatePricingDraft],
  );

  const handleIndividualMarkupToggle = useCallback(
    (enabled: boolean) => {
      if (!enabled && forcePerPiece) return;
      updatePricingDraft({ allowIndividualMarkups: enabled });
    },
    [forcePerPiece, updatePricingDraft],
  );

  const handleMarkupTypeChange = useCallback((type: 'PERCENTAGE' | 'AMOUNT') => {
    setPricingDraft((prev) => ({
      ...prev,
      markupType: normalizeAdjustmentType(type),
    }));
  }, []);

  const handleMarkupValueChange = useCallback((value: number) => {
    setPricingDraft((prev) => ({
      ...prev,
      markupValue: roundMoney(Math.max(0, value)),
    }));
  }, []);

  const handleProductMarkupChange = useCallback(
    (productId: number, type: 'PERCENTAGE' | 'AMOUNT', value: number) => {
      updateProductMarkup(productId, type, roundMoney(Math.max(0, value)));
    },
    [updateProductMarkup],
  );

  const handleDiscountToggle = useCallback(
    (enabled: boolean) => {
      if (!enabled) {
        updatePricingDraft({
          discountEnabled: false,
          allowIndividualDiscounts: false,
          discountType: 'AMOUNT',
          discountValue: 0,
        });
        return;
      }
      updatePricingDraft({ discountEnabled: true, allowIndividualDiscounts: forcePerPiece });
    },
    [forcePerPiece, updatePricingDraft],
  );

  const handleIndividualDiscountToggle = useCallback(
    (enabled: boolean) => {
      if (!enabled && forcePerPiece) return;
      updatePricingDraft({ allowIndividualDiscounts: enabled });
    },
    [forcePerPiece, updatePricingDraft],
  );

  const handleDiscountTypeChange = useCallback(
    (type: 'PERCENTAGE' | 'AMOUNT') => {
      setPricingDraft((prev) => {
        const discountType = normalizeAdjustmentType(type);
        return {
          ...prev,
          discountType,
          discountValue: clampUniformDiscountValue(
            discountType,
            prev.discountValue,
            projectedAdjustedProducts,
          ),
        };
      });
    },
    [projectedAdjustedProducts],
  );

  const handleDiscountValueChange = useCallback(
    (value: number) => {
      setPricingDraft((prev) => ({
        ...prev,
        discountValue: clampUniformDiscountValue(
          prev.discountType,
          value,
          projectedAdjustedProducts,
        ),
      }));
    },
    [projectedAdjustedProducts],
  );

  const handleProductDiscountChange = useCallback(
    (productId: number, type: 'PERCENTAGE' | 'AMOUNT' | 'NONE', value: number) => {
      updateProductDiscount(productId, type, value);
    },
    [updateProductDiscount],
  );

  const paymentsLocked = state.step2PaymentsLocked;
  const chargesSectionVisible = isEditMode ? state.pricingEditable : flowPhase >= 3;
  const chargesEditable = isEditMode ? state.pricingEditable : flowPhase === 3 && !paymentsLocked;
  const paymentConfigVisible = !isEditMode && flowPhase >= 4;
  const paymentConfigEditable = !isEditMode && flowPhase === 4 && !paymentsLocked;
  const paymentMethodsVisible = !isEditMode && flowPhase >= 5;
  const paymentMethodsEditable = !isEditMode && flowPhase === 5 && !paymentsLocked;
  /** @deprecated Prefer paymentMethodsEditable; kept for gradual call-site updates */
  const paymentsSectionVisible = paymentMethodsVisible;
  const paymentsEditable = paymentMethodsEditable;

  const handlePaymentMethodClick = useCallback(
    (method: string) => {
      if (!paymentsEditable) return;
      const isSinglePaymentMode = !state.paymentConfig.allowMultiplePayments;
      const isPartialOff = !state.paymentConfig.allowPartialPayment;
      if (isSinglePaymentMode && state.paymentConfig.payments.length >= 1) {
        return;
      }

      if (isSinglePaymentMode && isPartialOff) {
        if (method !== PAYMENT_METHODS.CASH) {
          const totalToCharge = orderSummary?.grandTotal ?? 0;
          addPayment({
            method,
            amount: roundMoney(totalToCharge),
          });
          return;
        }
      }

      setPendingPaymentMethod(method);
      setShowPaymentAmountDialog(true);
    },
    [
      addPayment,
      paymentsEditable,
      orderSummary,
      state.paymentConfig.allowMultiplePayments,
      state.paymentConfig.allowPartialPayment,
      state.paymentConfig.payments.length,
    ],
  );

  const handleOtherMethodSelect = useCallback(
    (method: PaymentMethod) => {
      if (!paymentsEditable) return;
      const isSinglePaymentMode = !state.paymentConfig.allowMultiplePayments;
      const isPartialOff = !state.paymentConfig.allowPartialPayment;
      if (isSinglePaymentMode && state.paymentConfig.payments.length >= 1) {
        return;
      }

      if (isSinglePaymentMode && isPartialOff) {
        const totalToCharge = orderSummary?.grandTotal ?? 0;
        addPayment({
          method: method.code,
          amount: roundMoney(totalToCharge),
        });
        setShowOtherMethodsDialog(false);
        return;
      }

      setPendingPaymentMethod(method.code);
      setShowOtherMethodsDialog(false);
      setShowPaymentAmountDialog(true);
    },
    [
      addPayment,
      paymentsEditable,
      orderSummary,
      state.paymentConfig.allowMultiplePayments,
      state.paymentConfig.allowPartialPayment,
      state.paymentConfig.payments.length,
    ],
  );

  const handleOpenOtherMethods = useCallback(() => {
    if (!paymentsEditable) return;
    if (otherPaymentMethods.length > 0) {
      setShowOtherMethodsDialog(true);
    }
  }, [paymentsEditable, otherPaymentMethods.length]);

  const handleCloseOtherMethods = useCallback(() => {
    setShowOtherMethodsDialog(false);
  }, []);

  const handleAddPayment = useCallback(
    (
      method: string,
      amount: number,
      cashReceived?: number,
      notes?: string,
      bankReference?: string,
      qrTransactionId?: string,
    ) => {
      const remainingBalance =
        orderSummary.grandTotal -
        state.paymentConfig.payments.reduce((sum, p) => sum + p.amount, 0);

      const paymentAmount = amount > 0 ? amount : Math.max(0, remainingBalance);

      addPayment({
        method,
        amount: roundMoney(paymentAmount),
        cashReceived: cashReceived != null ? roundMoney(cashReceived) : undefined,
        paymentNotes: notes,
        bankReference,
        qrTransactionId,
      });

      setShowPaymentAmountDialog(false);
      setPendingPaymentMethod(null);
    },
    [addPayment, orderSummary.grandTotal, state.paymentConfig.payments],
  );

  const handleRemovePayment = useCallback(
    (paymentId: string) => {
      if (!paymentsEditable) return;
      removePayment(paymentId);
    },
    [paymentsEditable, removePayment],
  );

  const handleUpdatePaymentAmount = useCallback(
    (
      paymentId: string,
      amount: number,
      cashReceived?: number,
      notes?: string,
      bankReference?: string,
      qrTransactionId?: string,
    ) => {
      const trimmedNotes = notes?.trim();
      updatePayment(paymentId, {
        amount: roundMoney(amount),
        cashReceived: cashReceived != null ? roundMoney(cashReceived) : undefined,
        paymentNotes: trimmedNotes != null && trimmedNotes !== '' ? trimmedNotes : undefined,
        bankReference,
        qrTransactionId,
      });
    },
    [updatePayment],
  );

  const handleEditPayment = useCallback(
    (paymentId: string) => {
      if (!paymentsEditable) return;
      setEditingPaymentId(paymentId);
      setShowPaymentAmountDialog(true);
    },
    [paymentsEditable],
  );

  const handleClosePaymentAmountDialog = useCallback(() => {
    setShowPaymentAmountDialog(false);
    setPendingPaymentMethod(null);
    setEditingPaymentId(null);
  }, []);

  const handlePartialPaymentToggle = useCallback(
    (enabled: boolean) => {
      updatePaymentConfig({ allowPartialPayment: enabled });
    },
    [updatePaymentConfig],
  );

  const handleMultiplePaymentsToggle = useCallback(
    (enabled: boolean) => {
      updatePaymentConfig({
        allowMultiplePayments: enabled,
        payments: enabled ? state.paymentConfig.payments : state.paymentConfig.payments.slice(0, 1),
      });
    },
    [state.paymentConfig.payments, updatePaymentConfig],
  );

  const formatAdjustmentDisplay = useCallback(
    (adjustmentType: string, adjustmentValue: number, basePrice: number) =>
      formatPriceAdjustmentDisplay(adjustmentType, adjustmentValue, basePrice),
    [],
  );

  const getTotalPayments = useCallback(() => {
    return state.paymentConfig.payments.reduce((sum, p) => sum + p.amount, 0);
  }, [state.paymentConfig.payments]);

  const getRemainingBalance = useCallback(() => {
    return Math.max(0, orderSummary.grandTotal - getTotalPayments());
  }, [orderSummary.grandTotal, getTotalPayments]);

  const configEditable = flowPhase === 1;
  const productsEditable = flowPhase === 2;
  const productsSectionActive = hasCommercialConfig && flowPhase >= 2;
  // Edit: charges are not a gated substep; continue once pricing is valid.
  const canProceed = canProceedToStep3 && !hasInvalidLinePricing && (isEditMode || flowPhase === 5);

  const handleNextStep = useCallback(() => {
    if (!canProceed) return;
    closePaymentUi();
    setStep2PaymentsLocked(true);
    nextStep();
  }, [canProceed, closePaymentUi, nextStep, setStep2PaymentsLocked]);

  return {
    state,
    orderSummary,
    flowPhase,
    isEditMode,
    configEditable,
    productsEditable,
    productsSectionActive,
    hasCommercialConfig,
    chargesSectionVisible,
    chargesEditable,
    paymentConfigVisible,
    paymentConfigEditable,
    paymentMethodsVisible,
    paymentMethodsEditable,
    paymentsSectionVisible,
    paymentsEditable,
    paymentsLocked,
    commitPricingConfig,
    confirmProductsSection,
    confirmChargesSection,
    confirmPaymentConfigSection,
    unlockConfigForEdit,
    unlockProductsForEdit,
    unlockChargesForEdit,
    unlockPaymentConfigForEdit,
    unlockPaymentMethodsForEdit,
    goToPreviousStep2Section,
    canConfirmConfigSection,
    canConfirmProductsSection,
    hasInvalidLinePricing,
    discountExceedsTotal,
    pricingDraft,
    maxUniformDiscountAmount,
    forcePerPiece,
    canProceed,
    paymentMethodOptions: PAYMENT_METHOD_OPTIONS,
    otherPaymentMethods,
    showOtherMethodsDialog,
    showPaymentAmountDialog,
    pendingPaymentMethod,
    editingPaymentId,
    payments: state.paymentConfig.payments,
    orderCharges: state.orderCharges,
    chargeCategories,
    addOrderCharge,
    updateOrderCharge,
    removeOrderCharge,
    handleMarkupToggle,
    handleIndividualMarkupToggle,
    handleMarkupTypeChange,
    handleMarkupValueChange,
    handleProductMarkupChange,
    handleDiscountToggle,
    handleIndividualDiscountToggle,
    handleDiscountTypeChange,
    handleDiscountValueChange,
    handleProductDiscountChange,
    handlePaymentMethodClick,
    handleOtherMethodSelect,
    handleOpenOtherMethods,
    handleCloseOtherMethods,
    handleAddPayment,
    handleRemovePayment,
    handleUpdatePaymentAmount,
    handleEditPayment,
    handleClosePaymentAmountDialog,
    handlePartialPaymentToggle,
    handleMultiplePaymentsToggle,
    handleNextStep,
    previousStep,
    formatAdjustmentDisplay,
    getPaymentMethodName,
    getTotalPayments,
    getRemainingBalance,
  };
};
